/**
 * Shared formatters. Use these instead of ad-hoc `toLocaleString()` calls so we
 * get consistent thousands separators / currency prefix across the whole app.
 */
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// ux9：统一日期出口需要 fromNow()，挂上 relativeTime 插件（幂等，多次 extend 无副作用）
dayjs.extend(relativeTime);

/** ux9 统一日期出口：入参 v 可为 string|number|Date|dayjs，空/null/无效一律返回 '-'。 */
type DateInput = string | number | Date | dayjs.Dayjs | null | undefined;

/** 内部：把入参转成有效 dayjs，无效返回 null。 */
function toValidDayjs(v: DateInput): dayjs.Dayjs | null {
  if (v === null || v === undefined || v === '') return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
}

/** 'YYYY-MM-DD HH:mm'，无效 '-'。 */
export function fmtDateTime(v: DateInput): string {
  const d = toValidDayjs(v);
  return d ? d.format('YYYY-MM-DD HH:mm') : '-';
}

/** 'YYYY-MM-DD'，无效 '-'。 */
export function fmtDate(v: DateInput): string {
  const d = toValidDayjs(v);
  return d ? d.format('YYYY-MM-DD') : '-';
}

/** dayjs().fromNow() 相对时间（如「3 小时前」），无效 '-'。 */
export function fmtRelative(v: DateInput): string {
  const d = toValidDayjs(v);
  return d ? d.fromNow() : '-';
}

/** 'M月D日'，无效 '-'。 */
export function fmtDateShort(v: DateInput): string {
  const d = toValidDayjs(v);
  return d ? d.format('M月D日') : '-';
}

/** 'MM-DD HH:mm'，无效 '-'。 */
export function fmtMonthDayTime(v: DateInput): string {
  const d = toValidDayjs(v);
  return d ? d.format('MM-DD HH:mm') : '-';
}

/** 'HH:mm'，无效 '-'。 */
export function fmtTime(v: DateInput): string {
  const d = toValidDayjs(v);
  return d ? d.format('HH:mm') : '-';
}

/** 'YYYY-MM-DD HH:mm:ss'（运维/授权时间需到秒），无效 '-'。 */
export function fmtDateTimeSec(v: DateInput): string {
  const d = toValidDayjs(v);
  return d ? d.format('YYYY-MM-DD HH:mm:ss') : '-';
}

/** "¥19,800" — for medium-sized amounts. Returns "—" for 0/null/undefined. */
export function formatMoney(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!isFinite(n) || n === 0) return '—';
  return `¥${n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`;
}

/** "¥4.2万" — for large totals shown on dashboards. */
export function formatMoneyShort(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!isFinite(n) || n === 0) return '¥0';
  if (n >= 10000) return `¥${(n / 10000).toFixed(1)}万`;
  return formatMoney(n);
}

/** Plain integer with thousands: 18 → "18", 19800 → "19,800". */
export function formatInt(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '0';
  return Math.round(Number(v)).toLocaleString('zh-CN');
}
